import React, {ChangeEvent, FC, useEffect, useState} from "react";
import {createOptionListForSelectTag, isNullOrEmpty, setNestedValue} from "../../../helpers/utils/Utils";
import {useGetEnumsQuery} from "../../../app/globalApi";
import {
    useGetAllRegularCustomersQuery,
    useLazyGetCustomerByIdQuery,
} from "../../customer/customerApi";
import InputField from "../../../components/common/form/InputField";
import SelectField from "../../../components/common/form/SelectField";
import Button from "../../../components/common/form/Button";
import _ from "lodash";
import {useLazyGetBranchByIdQuery, useLazyGetBranchesForCustomerQuery} from "../../customer/branchApi";
import IconButton from "../../../components/common/form/IconButton";
import {FaLink} from "react-icons/fa";
import {useLazyGetOrderListByCustomerQuery} from "../productsApi";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import {Joystick} from "lucide-react";
import {stringify} from "querystring";


export interface CreateOrderTabProps {
    createOrderData: any,
    setCreateOrderData: React.Dispatch<React.SetStateAction<any>>,
    setTab: React.Dispatch<React.SetStateAction<number>>,
    isReorder?: boolean,
    orderData?: any
}

const OrderDetailsTab: FC<CreateOrderTabProps> = ({createOrderData, setCreateOrderData, setTab, isReorder = false, orderData = null}) => {

    const allRegularCustomersResult = useGetAllRegularCustomersQuery("");
    const [getBranchesForCustomer, branchesForCustomerResult,] = useLazyGetBranchesForCustomerQuery();
    const [customerById] = useLazyGetCustomerByIdQuery();
    const [getBranchById, branchById] = useLazyGetBranchByIdQuery();
    const {data: enums} = useGetEnumsQuery("");
    const [productTypeEnums, setProductTypeEnums] = useState([]);
    const [sellers, setSellers] = useState([{sellerLastName: '', sellerFirstName: ''}]);
    const [buyers, setBuyers] = useState([{buyerLastName: '', buyerFirstName: ''}]);
    const [getOrderListByCustomerQuery, {data: getOrderListByCustomerData}] = useLazyGetOrderListByCustomerQuery();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [validationError, setValidationError] = useState(true);
    const [buyerValidationError, setBuyerValidationError] = useState(true);

    useEffect(() => {
        if (createOrderData.customerId) {
            getBranchesForCustomer({pageSize: 100, customerId: createOrderData.customerId?.value})
            customerById(createOrderData.customerId?.value);
            setCreateOrderData({
                ...createOrderData,
                branchId: null,
            })

            getOrderListByCustomerQuery({
                customerId: createOrderData.customerId?.value,
                customerOrderNumber: createOrderData.orderNumber,
            })
        }
    }, [createOrderData.customerId]);

    useEffect(() => {
        if (!createOrderData?.branchId && !isReorder) {
            setCreateOrderData({
                ...createOrderData,
                products: [],
            })

            setProductTypeEnums([]);
        }

        if (createOrderData.branchId && createOrderData.customerId) {
            setProductTypeEnums([]);
            getBranchById({customerId: createOrderData.customerId?.value, branchId: createOrderData.branchId?.value})
        }

    }, [createOrderData.branchId]);

    useEffect(() => {
        let customerList = allRegularCustomersResult?.data?.filter((el: any) => !el.parentCustomer);
        if (customerList?.length !== 1) return;

        createOrderData['customerId'] = {
            label: customerList[0]?.customerName,
            value: customerList[0]?.id
        }
        setCreateOrderData({
            ...createOrderData,
        })

    }, [allRegularCustomersResult]);

    useEffect(() => {
        if (!branchById?.data) return;
        if (branchById?.data.availableProduct.length === 0) {
            setProductTypeEnums([]);
            setCreateOrderData({
                ...createOrderData,
                products: []
            });
            return;
        }

        // @ts-ignore
        setProductTypeEnums(_.map(branchById?.data.availableProduct, (curValue) => (
            {
                value: enums?.["ProductType"].filter((el: any) => el.constant === curValue)[0]?.value,
                constant: enums?.["ProductType"].filter((el: any) => el.constant === curValue)[0]?.constant
            }
        )));


        if (!isReorder) {
            setCreateOrderData({
                ...createOrderData,
                products:
                    branchById?.data?.defaultProducts?.map((productConstant: string) => {
                        const match = enums?.["ProductType"]?.find(
                            (el: any) => el.constant === productConstant
                        );
                        return {
                            label: match?.value || productConstant,
                            value: productConstant,
                        };
                    }) || [],
            });

        }

    }, [branchById]);

    useEffect(() => {

        const branch = branchesForCustomerResult?.data?.data;
        let branchId: any = null;

        if (!branch) {
            return;
        }
        const defaultBranch = branch.find((curBranch: any) => curBranch.defaultBranch);
        if (isReorder && orderData != null) {
            branchId = {label: orderData?.data?.branchDTO?.name, value: orderData?.data?.branchDTO?.id};
        } else {
            if (defaultBranch) {
                branchId = {label: defaultBranch?.name, value: defaultBranch?.id};
            } else if (branch.length === 1) {
                branchId = {label: branch[0]?.name, value: branch[0]?.id};
            }
        }
        setCreateOrderData({...createOrderData, branchId: branchId});
    }, [branchesForCustomerResult]);


    const handleOnChange = (
        e: ChangeEvent<HTMLInputElement> | { name: string; value: string }
    ) => {
        const {name, value} = "target" in e ? e.target : e;

        setCreateOrderData((prevData: any) => {
            const updated = setNestedValue(prevData, name.split("."), value);

            // if top-level key is customerId, reset branchId
            const isCustomerChange = name === "customerId";

            return {
                ...prevData,
                ...updated,
                ...(isCustomerChange && {branchId: null}),
            };
        });
    };

    const isNextButtonEnabled = () => {
        return ["orderNumber", "customerId", "branchId", "products"]?.every(cur => !isNullOrEmpty(createOrderData?.[cur]));
    }

    const handleAddSeller = () => {
        const {sellerFirstName, sellerLastName} = sellers[0];

        if (!sellerFirstName && !sellerLastName) return;

        const fullName = sellerFirstName
            ? `${sellerFirstName} ${sellerLastName}`.trim()
            : sellerLastName;

        setCreateOrderData((prev: any) => ({
            ...prev,
            sellers: [...(prev.sellers || []), fullName]
        }));

        setSellers([{sellerFirstName: "", sellerLastName: ""}]);
        setValidationError(true);

    };


    const handleAddBuyer = () => {
        const {buyerFirstName, buyerLastName} = buyers[0];

        if (!buyerFirstName && !buyerLastName) return;

        const fullName = buyerFirstName
            ? `${buyerFirstName} ${buyerLastName}`.trim()
            : buyerLastName;

        setCreateOrderData((prev: any) => ({
            ...prev,
            buyers: [...(prev.buyers || []), fullName]
        }));

        setBuyers([{buyerFirstName: "", buyerLastName: ""}]);
        setBuyerValidationError(true);

    };


    const handleRemoveSeller = (index: number) => {
        const updatedSellers = createOrderData.sellers.filter((_: any, i: number) => i !== index);
        setCreateOrderData((prev: any) => ({
            ...prev,
            sellers: updatedSellers
        }));
    };

    const handleRemoveBuyer = (index: number) => {
        const updatedBuyers = createOrderData.buyers.filter((_: any, i: number) => i !== index);
        setCreateOrderData((prev: any) => ({
            ...prev,
            buyers: updatedBuyers
        }));
    };


    const handleSellerLastNameChange = (field: string, value: any) => {
        setSellers([{...sellers[0], sellerLastName: value}]);
        setValidationError(!value && !sellers[0]?.sellerFirstName);
    };

    const handleSellerFirstNameChange = (value: any) => {
        setSellers([{...sellers[0], sellerFirstName: value}]);
        setValidationError(!value && !sellers[0]?.sellerLastName);

    };

    const handleBuyerLastNameChange = (value: string) => {
        setBuyers([{...buyers[0], buyerLastName: value}]);
        setBuyerValidationError(!value && !buyers[0]?.buyerFirstName);

    };

    const handleBuyerFirstNameChange = (value: string) => {
        setBuyers([{...buyers[0], buyerFirstName: value}]);
        setBuyerValidationError(!value && !buyers[0]?.buyerLastName);

    };

    return (
        <>
            <InputField
                label="Order No"
                value={createOrderData?.orderNumber}
                placeholder="Enter Order No..."
                type="text"
                name={"orderNumber"}
                onChange={handleOnChange}
                required={true}
                disabled={isReorder}
            />
            <div className="col-span-12 grid grid-cols-12 gap-3">
                <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                    <label className="min-w-[100px]">Seller Name</label>
                </div>

                <div className="relative col-span-9 flex items-center gap-3">
                    <div className="w-full">
                        <InputField
                            placeholder="Enter Seller Last Name(s)..."
                            type="text"
                            name="taxYear"
                            value={sellers[0].sellerLastName}
                            onChange={(e) => handleSellerLastNameChange("sellerLastName", e.target.value)}
                            hideLabel={true}
                            className="w-full"
                        />
                    </div>

                    <div className="w-full">
                        <InputField
                            placeholder="Enter Seller First Name(s)..."
                            type="text"
                            name="taxRate"
                            value={sellers[0].sellerFirstName}
                            onChange={(e) => handleSellerFirstNameChange(e.target.value)}
                            hideLabel={true}
                            className="w-full"
                        />
                    </div>

                    <i
                        onClick={() => {
                            if (!validationError) {
                                handleAddSeller();
                            }
                        }
                        }
                        className={`fa fa-plus-circle m-md-0  mt-1 ${!validationError ? 'cursor-pointer text-highlight-0' : 'cursor-default'
                        }`}
                        id="add-seller"
                        title="Add Seller"

                    ></i>

                </div>
            </div>
            <div>
                {createOrderData.sellers?.map((seller: any, index: any) => (
                    <div key={index}>

                        <div className="relative pr-10 pl-4">
                            <InputField
                                label=""
                                value={seller}
                                placeholder="Enter Seller First Name(s)"
                                type="text"
                                name={"sellerFirstName"}
                                onChange={(e) => handleSellerFirstNameChange(e.target.value)}
                                disabled
                            />

                            <i onClick={() => handleRemoveSeller(index)}
                               className="fa fa-trash m-md-0 mt-5 ml-4 cursor-pointer absolute right-1 top-1"
                               title="Delete Seller"
                            ></i>
                        </div>

                    </div>
                ))}
            </div>

            <div className="col-span-12 grid grid-cols-12 gap-3">
                <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                    <label className="min-w-[100px]">Buyer Name</label>
                </div>

                <div className="relative col-span-9 flex items-center gap-3">
                    <div className="w-full">
                        <InputField
                            placeholder="Enter Buyer Last Name(s)..."
                            name={"buyerLastName"}
                            value={buyers[0].buyerLastName}
                            onChange={(e) => handleBuyerLastNameChange(e.target.value)}
                            hideLabel={true}
                            className="w-full"
                        />
                    </div>

                    <div className="w-full">
                        <InputField

                            placeholder="Enter Buyer First Name(s)"
                            type="text"
                            name={"buyerFirstName"}
                            value={buyers[0].buyerFirstName} hideLabel={true}
                            className="w-full" onChange={(e) => handleBuyerFirstNameChange(e.target.value)}
                        />
                    </div>

                    <i
                        onClick={() => {
                            if (!buyerValidationError) {
                                handleAddBuyer();
                            }
                        }
                        }
                        className={`fa fa-plus-circle m-md-0 mt-1 ${!buyerValidationError ? 'cursor-pointer text-highlight-0' : 'cursor-default'
                        }`}
                        id="add-seller"
                        title="Add Seller"

                    ></i>

                </div>
            </div>
            <div>
                {createOrderData.buyers?.map((buyer: any, index: any) => (
                    <div key={index}>

                        <div className="relative pr-10 pl-4">
                            <InputField
                                label=""
                                value={buyer}
                                placeholder="Enter Buyer First Name(s)"
                                type="text"
                                name={"buyerFirstName"}
                                onChange={(e) => handleSellerFirstNameChange(e.target.value)}
                                disabled
                            />

                            <i onClick={() => handleRemoveBuyer(index)}
                               className="fa fa-trash m-md-0 mt-5 ml-4 cursor-pointer absolute right-1 top-1"
                               title="Delete Buyer"
                            ></i>
                        </div>

                    </div>
                ))}
            </div>
            <InputField
                label="Closing Agent Full Name"
                value={createOrderData?.closingAgent?.fullName}
                placeholder="Enter Closing Agent Full Name"
                type="text"
                name={"closingAgent.fullName"}
                onChange={handleOnChange}


            />
            <InputField
                label="Closing Agent Email"
                value={createOrderData?.closingAgent?.email}
                placeholder="Enter Closing Agent Email"
                type="text"
                name={"closingAgent.email"}
                onChange={handleOnChange}


            />
            <SelectField
                label="Customer"
                value={createOrderData?.customerId}
                placeholder="Select Customer..."
                options={createOptionListForSelectTag(allRegularCustomersResult?.data?.filter((el: any) => !el.parentCustomer), "customerName", "id", "customerNumber")}
                onChange={(selectedOption: any) => handleOnChange({name: 'customerId', value: selectedOption})}
                required={true}
                icon={<IconButton
                    icon={<FaLink/>}
                    className="text-highlight-4 hover:text-highlight-7 text-sm"
                    onClick={() => {
                        const url = createOrderData?.customerId
                            ? `/customers/${createOrderData.customerId.value}`
                            : "/customers";

                        window.open(url, '_blank');
                    }}
                />}
                isDisabled={isReorder}
            />

            <SelectField
                label="Branch"
                value={createOrderData?.branchId}
                placeholder="Select Branch..."
                options={createOptionListForSelectTag(branchesForCustomerResult?.data?.data, "name", "id", "", "displayLabel")}
                onChange={(selectedOption: any) => handleOnChange({name: 'branchId', value: selectedOption})}
                required={true}
                isLoading={isNullOrEmpty(createOrderData?.customerId) || branchesForCustomerResult.isLoading || branchesForCustomerResult.isFetching}
                icon={<IconButton
                    icon={<FaLink/>}
                    className={
                        createOrderData?.branchId
                            ? "text-highlight-4 hover:text-highlight-7 text-sm"
                            : (createOrderData?.customerId
                                ? "text-highlight-4 hover:text-highlight-7 text-sm"
                                : "text-surface-4 text-sm")
                    }
                    onClick={() => {
                        const customerId = createOrderData?.customerId?.value;
                        const branchId = createOrderData?.branchId?.value;

                        if (customerId) {
                            const url = branchId
                                ? `/customers/${customerId}/${branchId}`
                                : `/customers/${customerId}`;
                            window.open(url, '_blank');
                        }
                    }}
                />
                }
            />

            <SelectField
                label="Product"
                value={createOrderData?.products}
                placeholder="Select Product Type..."
                options={createOptionListForSelectTag(
                    enums?.["ProductType"]?.filter(
                        (el: any) => el.constant !== "NTP" && el.constant !== "UNKNOWN"
                    ),
                    "value",
                    "constant"
                )}
                onChange={(selectedOption: any) => handleOnChange({ name: 'products', value: selectedOption })}
                required={true}
                isDisabled={!productTypeEnums || productTypeEnums.length === 0}
                isMulti
            />

            <div className={"flex justify-end text-end mt-4"}>
                <Button btnText={"Next"} isLoading={false} type={isNextButtonEnabled() ? "submit" : "button"}
                        onClick={() => {
                            if (isReorder) {
                                setTab(2)
                            } else {
                                if (getOrderListByCustomerData?.length > 0) {
                                    setShowConfirmation(true)
                                } else {
                                    setTab(2)
                                }
                            }
                        }}
                        isDisabled={!isNextButtonEnabled()}
                />
            </div>

            {
                showConfirmation ?
                    <ConfirmationModal title={"Reorder Confirmation"}
                                       bodyMessage={"This order already exists, are you sure you want to request an update?"}
                                       onSubmit={() => {
                                           setTab(2)
                                       }} show={showConfirmation}
                                       setShow={setShowConfirmation}
                    /> : <></>
            }
        </>
    )
}
export default OrderDetailsTab